import * as Sentry from "@sentry/react";

// Arrays longer than this are collapsed to a summary in breadcrumbs. The full store on every
// dispatch (e.g. document/appointment lists) × ~100 retained breadcrumbs blows past Sentry's
// payload-size limit, which silently drops breadcrumbs — the summary keeps the store legible.
const ARRAY_SUMMARY_THRESHOLD = 25;
// A few head items are kept so the array's shape stays visible in the breadcrumb.
const ARRAY_SAMPLE_SIZE = 3;
// Bounds the walk so a deeply nested (or unexpectedly circular) value can't spin.
const MAX_WALK_DEPTH = 12;

const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  (Object.getPrototypeOf(value) === Object.prototype ||
    Object.getPrototypeOf(value) === null);

// Returns a copy of `value` with over-threshold arrays collapsed to
// { _summary, length, sample }. Non-plain objects (Date, Timestamp, class instances) are treated
// as leaves so we never deep-walk foreign structures.
const summarizeLargeArrays = (value, depth = 0) => {
  if (depth >= MAX_WALK_DEPTH) return value;

  if (Array.isArray(value)) {
    if (value.length > ARRAY_SUMMARY_THRESHOLD) {
      return {
        _summary: `Array(${value.length}) — summarized`,
        length: value.length,
        sample: value
          .slice(0, ARRAY_SAMPLE_SIZE)
          .map((item) => summarizeLargeArrays(item, depth + 1))
      };
    }
    return value.map((item) => summarizeLargeArrays(item, depth + 1));
  }

  if (isPlainObject(value)) {
    const out = {};
    for (const key of Object.keys(value)) {
      out[key] = summarizeLargeArrays(value[key], depth + 1);
    }
    return out;
  }

  return value;
};

// Records one Sentry breadcrumb per dispatch: the action + the FULL redux store after it applied
// (heavy arrays summarized). Only the resulting state is kept — breadcrumbs are ordered, so the
// previous action's breadcrumb already holds the "before" state, and skipping the pre-snapshot +
// diff avoids doing that work twice. No-op unless Sentry is initialized, so local dev pays nothing.
export const reduxBreadcrumbMiddleware = (store) => (next) => (action) => {
  if (!Sentry.getClient()) return next(action);

  const result = next(action);

  try {
    Sentry.addBreadcrumb({
      category: "redux",
      type: "info",
      level: "info",
      message: action.type,
      data: {
        action: summarizeLargeArrays(action),
        state: summarizeLargeArrays(store.getState())
      }
    });
  } catch {
    // breadcrumb recording must never break dispatch
  }

  return result;
};
